import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Facebook, 
  Linkedin,
  Instagram,
  ArrowRight 
} from 'lucide-react';
import { useSiteContent } from '../../context/SiteContentContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = '/images/logo.png';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { language, localizedPath } = useSiteContent();
  const [settings, setSettings] = useState({
    company_email: 'info@tetralog.bg',
    company_phone: '+359 887 198 834',
    company_phone_2: '',
    address_1_name: 'Tetralog Ltd.',
    address_1_street: 'Tsar Osvoboditel 88V',
    address_1_city: '4400 Pazardzhik, Bulgaria',
    address_2_name: '',
    address_2_street: '',
    address_2_city: '',
    address_1_maps_url: '',
    address_2_maps_url: '',
    footer_description_en: '',
    footer_description_bg: '',
    footer_facebook_url: '',
    footer_linkedin_url: '',
    footer_instagram_url: '',
    footer_copyright_en: '',
    footer_copyright_bg: ''
  });
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, servicesRes] = await Promise.all([
          axios.get(`${API}/settings`),
          axios.get(`${API}/services`)
        ]);
        setSettings(settingsRes.data);
        // Filter and sort services for footer (top 5 active)
        const activeServices = (servicesRes.data.services || [])
          .filter(s => s.is_active)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .slice(0, 5);
        setServices(activeServices);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Default descriptions if not set in admin
  const defaultDescriptionEn = 'Your trusted partner for international transport and logistics solutions. Over 20 years of experience delivering excellence across Europe.';
  const defaultDescriptionBg = 'Вашият доверен партньор за международен транспорт и логистични решения. Над 20 години опит в доставките в цяла Европа.';
  const defaultDescriptionDe = 'Ihr zuverlässiger Partner für internationale Transport- und Logistiklösungen. Über 20 Jahre Erfahrung in der Lieferung von Exzellenz in ganz Europa.';
  
  const description = language === 'bg' 
    ? (settings.footer_description_bg || defaultDescriptionBg)
    : language === 'de'
    ? (settings.footer_description_de || defaultDescriptionDe)
    : (settings.footer_description_en || defaultDescriptionEn);

  // Translations by language
  const translations = {
    bg: {
      description: 'Вашият доверен партньор за международен транспорт и логистични решения. Над 20 години опит в доставките в цяла Европа.',
      servicesTitle: 'Услуги',
      companyTitle: 'Компания',
      contactTitle: 'Свържете се',
      services: [
        { name: 'Специализиран товар', href: '/services#specialized-cargo' },
        { name: 'Международно преместване', href: '/services#international-moving' },
        { name: 'Експресен транспорт', href: '/services#express-transportation' },
        { name: 'Групажна линия', href: '/services#groupage-line' },
        { name: 'Лични вещи', href: '/services#personal-belongings' },
      ],
      company: [
        { name: 'За нас', href: '/about' },
        { name: 'Нашият флот', href: '/fleet' },
        { name: 'Дестинации', href: '/destinations' },
        { name: 'Кариери', href: '/careers' },
        { name: 'Новини', href: '/news' },
        { name: 'ЧЗВ', href: '/faq' },
        { name: 'Контакт', href: '/contact' },
      ],
      legal: [
        { name: 'Политика за поверителност', href: '/legal/privacy-policy' },
        { name: 'Условия за ползване', href: '/legal/terms-of-service' },
        { name: 'Политика за бисквитки', href: '/legal/cookie-policy' },
      ],
      copyright: `© ${currentYear} Тетралог ЕООД. Всички права запазени.`,
      location: 'София, България',
      locationDetail: 'Индустриална зона Запад'
    },
    en: {
      description: 'Your trusted partner for international transport and logistics solutions. Over 20 years of experience delivering excellence across Europe.',
      servicesTitle: 'Services',
      companyTitle: 'Company',
      contactTitle: 'Contact Us',
      services: [
        { name: 'Specialized Cargo', href: '/services#specialized-cargo' },
        { name: 'International Moving', href: '/services#international-moving' },
        { name: 'Express Transportation', href: '/services#express-transportation' },
        { name: 'Groupage Line', href: '/services#groupage-line' },
        { name: 'Personal Belongings', href: '/services#personal-belongings' },
      ],
      company: [
        { name: 'About Us', href: '/about' },
        { name: 'Our Fleet', href: '/fleet' },
        { name: 'Destinations', href: '/destinations' },
        { name: 'Careers', href: '/careers' },
        { name: 'News', href: '/news' },
        { name: 'FAQ', href: '/faq' },
        { name: 'Contact', href: '/contact' },
      ],
      legal: [
        { name: 'Privacy Policy', href: '/legal/privacy-policy' },
        { name: 'Terms of Service', href: '/legal/terms-of-service' },
        { name: 'Cookie Policy', href: '/legal/cookie-policy' },
      ],
      copyright: `© ${currentYear} Tetralog Ltd. All rights reserved.`,
      location: '4400 Pazardzhik, Bulgaria',
      locationDetail: 'Tsar Osvoboditel 88V'
    },
    de: {
      description: 'Ihr zuverlässiger Partner für internationale Transport- und Logistiklösungen. Über 20 Jahre Erfahrung in der Lieferung von Exzellenz in ganz Europa.',
      servicesTitle: 'Dienstleistungen',
      companyTitle: 'Unternehmen',
      contactTitle: 'Kontakt',
      services: [
        { name: 'Spezialtransporte', href: '/services#specialized-cargo' },
        { name: 'Internationale Umzüge', href: '/services#international-moving' },
        { name: 'Expresstransport', href: '/services#express-transportation' },
        { name: 'Sammelgutverkehr', href: '/services#groupage-line' },
        { name: 'Persönliche Gegenstände', href: '/services#personal-belongings' },
      ],
      company: [
        { name: 'Über uns', href: '/about' },
        { name: 'Unsere Flotte', href: '/fleet' },
        { name: 'Transportziele', href: '/destinations' },
        { name: 'Karriere', href: '/careers' },
        { name: 'Nachrichten', href: '/news' },
        { name: 'FAQ', href: '/faq' },
        { name: 'Kontakt', href: '/contact' },
      ],
      legal: [
        { name: 'Datenschutz', href: '/legal/privacy-policy' },
        { name: 'Nutzungsbedingungen', href: '/legal/terms-of-service' },
        { name: 'Cookie-Richtlinie', href: '/legal/cookie-policy' },
      ],
      copyright: `© ${currentYear} Tetralog GmbH. Alle Rechte vorbehalten.`,
      location: '4400 Pazardzhik, Bulgarien',
      locationDetail: 'Tsar Osvoboditel 88V'
    }
  };

  const content = translations[language] || translations.en;

  return (
    <footer 
      data-testid="main-footer"
      className="bg-white border-t border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to={localizedPath('/')} className="flex items-center mb-6">
              <img 
                src={LOGO_URL} 
                alt="TetraLog" 
                className="h-12 w-auto"
              />
            </Link>
            <p className="text-slate-500 text-sm mb-6 leading-relaxed">
              {description}
            </p>
            <div className="flex gap-4">
              {settings.footer_facebook_url && (
                <a 
                  href={settings.footer_facebook_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-[#C41E3A] hover:text-white transition-all"
                  data-testid="social-facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {settings.footer_linkedin_url && (
                <a 
                  href={settings.footer_linkedin_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-[#C41E3A] hover:text-white transition-all"
                  data-testid="social-linkedin"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              )}
              {settings.footer_instagram_url && (
                <a 
                  href={settings.footer_instagram_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-[#C41E3A] hover:text-white transition-all"
                  data-testid="social-instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {/* Show default icons if no social links configured */}
              {!settings.footer_facebook_url && !settings.footer_linkedin_url && !settings.footer_instagram_url && (
                <>
                  <a 
                    href="#" 
                    className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-[#C41E3A] hover:text-white transition-all"
                    data-testid="social-facebook"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                  <a 
                    href="#" 
                    className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-[#C41E3A] hover:text-white transition-all"
                    data-testid="social-linkedin"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                </>
              )}
            </div>
          </div>

          {/* Services - Dynamic from database */}
          <div>
            <h4 className="text-slate-900 font-bold uppercase tracking-wider text-sm mb-6">
              {content.servicesTitle}
            </h4>
            <ul className="space-y-3">
              {services.length > 0 ? services.map((service) => (
                <li key={service.id}>
                  <Link 
                    to={localizedPath(`/services#${service.id}`)}
                    className="text-slate-500 text-sm hover:text-[#C41E3A] transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {service[`title_${language}`] || service.title_en || service.title}
                  </Link>
                </li>
              )) : content.services.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={localizedPath(link.href)}
                    className="text-slate-500 text-sm hover:text-[#C41E3A] transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-slate-900 font-bold uppercase tracking-wider text-sm mb-6">
              {content.companyTitle}
            </h4>
            <ul className="space-y-3">
              {content.company.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={localizedPath(link.href)}
                    className="text-slate-500 text-sm hover:text-[#C41E3A] transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-slate-900 font-bold uppercase tracking-wider text-sm mb-6">
              {content.contactTitle}
            </h4>
            <ul className="space-y-4">
              {settings.company_phone && (
                <li>
                  <a 
                    href={`tel:${settings.company_phone.replace(/\s/g, '')}`}
                    className="flex items-start gap-3 text-slate-500 hover:text-[#C41E3A] transition-colors"
                    data-testid="footer-phone"
                  >
                    <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{settings.company_phone}</span>
                  </a>
                </li>
              )}
              {settings.company_phone_2 && (
                <li>
                  <a 
                    href={`tel:${settings.company_phone_2.replace(/\s/g, '')}`}
                    className="flex items-start gap-3 text-slate-500 hover:text-[#C41E3A] transition-colors"
                    data-testid="footer-phone-2"
                  >
                    <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{settings.company_phone_2}</span>
                  </a>
                </li>
              )}
              <li>
                <a 
                  href={`mailto:${settings.company_email || 'info@tetralog.bg'}`}
                  className="flex items-start gap-3 text-slate-500 hover:text-[#C41E3A] transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{settings.company_email || 'info@tetralog.bg'}</span>
                </a>
              </li>
              {(settings.address_1_name || settings.address_1_street || settings.address_1_city) && (
                <li>
                  {settings.address_1_maps_url ? (
                    <a 
                      href={settings.address_1_maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 text-slate-500 hover:text-[#C41E3A] transition-colors"
                    >
                      <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">
                        {settings.address_1_name && <>{settings.address_1_name}<br /></>}
                        {settings.address_1_street && <>{settings.address_1_street}<br /></>}
                        {settings.address_1_city}
                      </span>
                    </a>
                  ) : (
                    <div className="flex items-start gap-3 text-slate-500">
                      <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">
                        {settings.address_1_name && <>{settings.address_1_name}<br /></>}
                        {settings.address_1_street && <>{settings.address_1_street}<br /></>}
                        {settings.address_1_city}
                      </span>
                    </div>
                  )}
                </li>
              )}
              {(settings.address_2_name || settings.address_2_street || settings.address_2_city) && (
                <li>
                  {settings.address_2_maps_url ? (
                    <a 
                      href={settings.address_2_maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 text-slate-500 hover:text-[#C41E3A] transition-colors"
                    >
                      <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">
                        {settings.address_2_name && <>{settings.address_2_name}<br /></>}
                        {settings.address_2_street && <>{settings.address_2_street}<br /></>}
                        {settings.address_2_city}
                      </span>
                    </a>
                  ) : (
                    <div className="flex items-start gap-3 text-slate-500">
                      <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">
                        {settings.address_2_name && <>{settings.address_2_name}<br /></>}
                        {settings.address_2_street && <>{settings.address_2_street}<br /></>}
                        {settings.address_2_city}
                      </span>
                    </div>
                  )}
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-400 text-sm">
            {language === 'bg' 
              ? ((settings.footer_copyright_bg || `© {year} Тетралог ЕООД. Всички права запазени.`).replace('{year}', currentYear))
              : language === 'de'
              ? ((settings.footer_copyright_de || `© {year} Tetralog EOOD. Alle Rechte vorbehalten.`).replace('{year}', currentYear))
              : ((settings.footer_copyright_en || `© {year} Tetralog Ltd. All rights reserved.`).replace('{year}', currentYear))
            }
          </p>
          <div className="flex items-center gap-6">
            {content.legal.map((link) => (
              <Link 
                key={link.name}
                to={localizedPath(link.href)}
                className="text-slate-400 text-xs hover:text-slate-600 transition-colors"
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};
